import { POLICY_PAGES_DEFAULTS } from './policyDefaults';
import { AUTH_COPY_DEFAULTS } from './authCopyDefaults';
import { STORE_COPY_DEFAULTS } from './storeCopyDefaults';
import { GIFT_FINDER_DEFAULTS } from './giftFinderDefaults';
import { CUSTOM_JEWELRY_DEFAULTS } from '../jewelry/customJewelryDefaults';
import { mergeHeroActions } from '../storefrontHeroActions';
import { mergeStorefrontMerchandising } from './storefrontMerchandisingMerge';
import { normalizeGiftGroupsList, normalizeGiftRecipientsList } from './giftFinderNormalize';
import { normalizeBespokeJewelryTypes, normalizeBespokeOccasions, normalizeCustomJewelryTrustItems, normalizeCustomJewelrySteps } from './bespokeFormNormalize';
import { BLOG_POSTS } from './blogPosts';
import {
  HOME_COPY_DEFAULTS,
  ABOUT_COPY_DEFAULTS,
  CONTACT_COPY_DEFAULTS,
  BRAND_COPY_DEFAULTS,
  FOOTER_COPY_DEFAULTS,
  PAGE_COPY_DEFAULTS,
} from './pageCopyDefaults';
import { normalizeHeroSlide } from './homeHeroSlides';
import { HOME_HERO_SLIDES_MAX } from './heroSlidesDefaults';
import { buildDemoHeroSlides, shouldApplyDemoHeroInDev } from './demoHeroPhotoshoot';

const POLICY_SLUGS = ['shipping', 'returns', 'privacy'];
const MAX_BLOG_POSTS = 6;

export const BLOG_CONTENT_DEFAULTS = {
  section_title: 'Gift & styling ideas',
  section_subtitle: 'Tips for choosing Rakhi and Lumba — from our studio in Ahmedabad.',
  posts: BLOG_POSTS,
};

function normalizePolicySection(sec) {
  if (!sec || typeof sec !== 'object') return null;
  const heading = String(sec.heading || '').trim();
  const body = String(sec.body || '').trim();
  if (!heading && !body) return null;
  return { heading, body };
}

function normalizePolicyPage(page, slug) {
  const d = POLICY_PAGES_DEFAULTS[slug] || {};
  const src = page && typeof page === 'object' ? page : {};
  const sections = Array.isArray(src.sections)
    ? src.sections.map(normalizePolicySection).filter(Boolean)
    : [];
  return {
    slug,
    title: String(src.title || d.title || slug).trim() || d.title || slug,
    seoDescription: String(src.seoDescription || d.seoDescription || '').trim(),
    sections: sections.length ? sections : d.sections || [],
  };
}

export function mergePolicies(fromApi) {
  const src = fromApi && typeof fromApi === 'object' ? fromApi : {};
  const out = {};
  for (const slug of POLICY_SLUGS) {
    out[slug] = normalizePolicyPage(src[slug], slug);
  }
  return out;
}

export function policyBySlug(slug, policies) {
  const map = policies && typeof policies === 'object' ? policies : mergePolicies(null);
  return map[slug] || null;
}

function normalizeBenefits(list) {
  if (!Array.isArray(list)) return AUTH_COPY_DEFAULTS.benefits;
  const items = list.map((s) => String(s || '').trim()).filter(Boolean);
  return items.length ? items.slice(0, 8) : AUTH_COPY_DEFAULTS.benefits;
}

export function mergeAuthCopy(fromApi) {
  const src = fromApi && typeof fromApi === 'object' ? fromApi : {};
  const out = { ...AUTH_COPY_DEFAULTS };
  for (const key of Object.keys(AUTH_COPY_DEFAULTS)) {
    if (key === 'benefits') {
      out.benefits = normalizeBenefits(src.benefits);
      continue;
    }
    const val = src[key];
    if (val != null && String(val).trim() !== '') {
      out[key] = String(val).trim();
    }
  }
  return out;
}

function normalizeShopFeature(item, index) {
  const d = STORE_COPY_DEFAULTS.shop_features[index] || { icon: '', title: '', desc: '' };
  if (!item || typeof item !== 'object') return { ...d };
  return {
    icon: String(item.icon ?? d.icon).trim() || d.icon,
    title: String(item.title ?? d.title).trim() || d.title,
    desc: String(item.desc ?? d.desc).trim() || d.desc,
  };
}

function coerceBool(val, fallback) {
  if (val === true || val === 1 || val === '1' || val === 'true') return true;
  if (val === false || val === 0 || val === '0' || val === 'false') return false;
  return fallback;
}

export function mergeGiftFinderConfig(fromApi) {
  const src = fromApi && typeof fromApi === 'object' ? fromApi : {};
  const out = { ...GIFT_FINDER_DEFAULTS };
  const boolKeys = [
    'enabled',
    'show_on_home',
    'show_on_shop',
    'show_in_nav',
    'show_in_footer',
    'show_group_step',
    'show_budget_step',
    'show_recipient_step',
    'use_admin_gift_groups',
    'use_product_tags',
    'auto_widen_results',
  ];
  for (const key of boolKeys) {
    if (Object.prototype.hasOwnProperty.call(src, key)) {
      out[key] = coerceBool(src[key], out[key]);
    }
  }
  const skipKeys = new Set([...boolKeys, 'gift_groups', 'recipients']);
  for (const key of Object.keys(GIFT_FINDER_DEFAULTS)) {
    if (skipKeys.has(key)) continue;
    const val = src[key];
    if (val != null && String(val).trim() !== '') {
      out[key] = String(val).trim();
    }
  }
  out.gift_groups = normalizeGiftGroupsList(src.gift_groups);
  out.recipients = normalizeGiftRecipientsList(src.recipients);
  return out;
}

export function mergeCustomJewelryConfig(fromApi) {
  const src = fromApi && typeof fromApi === 'object' ? fromApi : {};
  const out = { ...CUSTOM_JEWELRY_DEFAULTS };
  const boolKeys = [
    'enabled',
    'show_on_shop',
    'show_in_nav',
    'show_in_footer',
    'show_on_gift_finder',
  ];
  const arrayKeys = new Set(['occasions', 'jewelry_types', 'trust_items', 'how_it_works_steps']);
  for (const key of boolKeys) {
    if (Object.prototype.hasOwnProperty.call(src, key)) {
      out[key] = coerceBool(src[key], out[key]);
    }
  }
  for (const key of Object.keys(CUSTOM_JEWELRY_DEFAULTS)) {
    if (boolKeys.includes(key) || arrayKeys.has(key)) continue;
    const val = src[key];
    if (val != null && String(val).trim() !== '') {
      out[key] = String(val).trim();
    }
  }
  out.occasions = normalizeBespokeOccasions(src.occasions);
  out.jewelry_types = normalizeBespokeJewelryTypes(src.jewelry_types);
  out.trust_items = normalizeCustomJewelryTrustItems(src.trust_items, CUSTOM_JEWELRY_DEFAULTS.trust_items);
  out.how_it_works_steps = normalizeCustomJewelrySteps(
    src.how_it_works_steps,
    CUSTOM_JEWELRY_DEFAULTS.how_it_works_steps
  );
  if (!out.breadcrumb_current_label) {
    out.breadcrumb_current_label = out.nav_label;
  }
  return out;
}

export function mergeStoreCopy(fromApi) {
  const src = fromApi && typeof fromApi === 'object' ? fromApi : {};
  const out = { ...STORE_COPY_DEFAULTS };
  const skip = new Set([
    'shop_features',
    'pdp_bullets',
    'gift_finder',
    'custom_jewelry',
    'hero_actions',
    'product_card_badges',
    'home_merchandising_tiles',
    'home_testimonials',
    'footer_popular_searches',
    'home_product_shelves',
  ]);
  const boolKeys = new Set([
    'home_personalization_enabled',
    'home_assistant_enabled',
    'home_trust_strip_enabled',
    'home_merchandising_enabled',
    'home_testimonials_enabled',
    'footer_popular_searches_enabled',
  ]);
  for (const key of Object.keys(STORE_COPY_DEFAULTS)) {
    if (key === 'shop_features') {
      const feats = Array.isArray(src.shop_features) ? src.shop_features : [];
      out.shop_features = [0, 1, 2].map((i) => normalizeShopFeature(feats[i], i));
      continue;
    }
    if (key === 'pdp_bullets') {
      const bullets = Array.isArray(src.pdp_bullets) ? src.pdp_bullets : [];
      out.pdp_bullets = [0, 1, 2].map((i) => {
        const def = STORE_COPY_DEFAULTS.pdp_bullets[i] || { text: '', link_label: '', link_href: '' };
        const item = bullets[i] || {};
        return {
          text: String(item.text ?? def.text).trim() || def.text,
          link_label: String(item.link_label ?? def.link_label).trim(),
          link_href: String(item.link_href ?? def.link_href).trim(),
        };
      });
      continue;
    }
    if (skip.has(key)) continue;
    if (boolKeys.has(key) && Object.prototype.hasOwnProperty.call(src, key)) {
      out[key] = coerceBool(src[key], out[key]);
      continue;
    }
    const val = src[key];
    if (val != null && String(val).trim() !== '') {
      out[key] = String(val).trim();
    }
  }
  out.gift_finder = mergeGiftFinderConfig(src.gift_finder);
  out.custom_jewelry = mergeCustomJewelryConfig(src.custom_jewelry);
  out.hero_actions = mergeHeroActions(src.hero_actions);
  Object.assign(out, mergeStorefrontMerchandising(src));
  return out;
}

function mergeStringMap(defaults, src, skipKeys = []) {
  const s = src && typeof src === 'object' ? src : {};
  const out = { ...defaults };
  for (const key of Object.keys(defaults)) {
    if (skipKeys.includes(key)) continue;
    if (Array.isArray(defaults[key])) continue;
    const val = s[key];
    if (val != null && String(val).trim() !== '') {
      out[key] = String(val).trim();
    }
  }
  return out;
}

function normalizeValueCard(item, index) {
  const d = ABOUT_COPY_DEFAULTS.values[index] || { icon: '', title: '', desc: '' };
  if (!item || typeof item !== 'object') return { ...d };
  return {
    icon: String(item.icon ?? d.icon).trim() || d.icon,
    title: String(item.title ?? d.title).trim() || d.title,
    desc: String(item.desc ?? d.desc).trim() || d.desc,
  };
}

function normalizeContactCard(item, index) {
  const d = CONTACT_COPY_DEFAULTS.cards[index] || { icon: '', title: '', desc: '', link_label: '' };
  if (!item || typeof item !== 'object') return { ...d };
  return {
    icon: String(item.icon ?? d.icon).trim() || d.icon,
    title: String(item.title ?? d.title).trim() || d.title,
    desc: String(item.desc ?? d.desc).trim() || d.desc,
    link_label: String(item.link_label ?? d.link_label).trim(),
  };
}

export function mergeHomeCopy(fromApi) {
  const src = fromApi && typeof fromApi === 'object' ? fromApi : {};
  const out = mergeStringMap(HOME_COPY_DEFAULTS, src, [
    'hero_slides',
    'hero_layout',
    'hero_autoplay_seconds',
  ]);
  const layoutRaw = String(src.hero_layout || '').trim().toLowerCase();
  out.hero_layout = layoutRaw === 'immersive' ? 'immersive' : HOME_COPY_DEFAULTS.hero_layout;
  const ap = Number(src.hero_autoplay_seconds);
  out.hero_autoplay_seconds = Number.isFinite(ap) ? Math.min(30, Math.max(0, Math.round(ap))) : HOME_COPY_DEFAULTS.hero_autoplay_seconds;
  const rawSlides = Array.isArray(src.hero_slides) ? src.hero_slides : [];
  out.hero_slides = Array.from({ length: HOME_HERO_SLIDES_MAX }, (_, i) =>
    normalizeHeroSlide(rawSlides[i] || {}, i)
  );
  if (shouldApplyDemoHeroInDev(out.hero_slides)) {
    const demo = buildDemoHeroSlides();
    out.hero_slides = Array.from({ length: HOME_HERO_SLIDES_MAX }, (_, i) =>
      normalizeHeroSlide(demo[i] || {}, i)
    );
    out.hero_layout = 'immersive';
    out.hero_autoplay_seconds = 5;
  }
  return out;
}

export function mergeAboutCopy(fromApi) {
  const src = fromApi && typeof fromApi === 'object' ? fromApi : {};
  const out = mergeStringMap(ABOUT_COPY_DEFAULTS, src, ['values']);
  const values = Array.isArray(src.values) ? src.values : [];
  out.values = [0, 1, 2].map((i) => normalizeValueCard(values[i], i));
  return out;
}

export function mergeContactCopy(fromApi) {
  const src = fromApi && typeof fromApi === 'object' ? fromApi : {};
  const out = mergeStringMap(CONTACT_COPY_DEFAULTS, src, ['cards']);
  const cards = Array.isArray(src.cards) ? src.cards : [];
  out.cards = [0, 1, 2, 3].map((i) => normalizeContactCard(cards[i], i));
  return out;
}

export function mergeBrandCopy(fromApi) {
  const src = fromApi && typeof fromApi === 'object' ? fromApi : {};
  const out = mergeStringMap(BRAND_COPY_DEFAULTS, src, ['header_show_logo']);
  if (Object.prototype.hasOwnProperty.call(src, 'header_show_logo')) {
    out.header_show_logo = coerceBool(src.header_show_logo, BRAND_COPY_DEFAULTS.header_show_logo);
  }
  return out;
}

export function mergePageCopy(fromApi) {
  const src = fromApi && typeof fromApi === 'object' ? fromApi : {};
  return {
    brand: mergeBrandCopy(src.brand),
    footer: mergeStringMap(FOOTER_COPY_DEFAULTS, src.footer),
    home: mergeHomeCopy(src.home),
    about: mergeAboutCopy(src.about),
    contact: mergeContactCopy(src.contact),
  };
}

export function pageCopySection(pageCopy, section) {
  const merged = mergePageCopy(pageCopy);
  return merged[section] || PAGE_COPY_DEFAULTS[section] || {};
}

function normalizeBlogSection(sec) {
  if (!sec || typeof sec !== 'object') return null;
  const heading = String(sec.heading || '').trim();
  const body = String(sec.body || '').trim();
  if (!heading && !body) return null;
  return { heading, body };
}

function normalizeBlogSlug(raw, fallback) {
  const slugRaw = String(raw ?? fallback ?? '')
    .trim()
    .toLowerCase();
  const slug = slugRaw.replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
  return slug || String(fallback || 'article').trim();
}

function normalizeBlogPost(post, fallback) {
  const d = fallback && typeof fallback === 'object' ? fallback : {};
  const src = post && typeof post === 'object' ? post : {};
  const sections = Array.isArray(src.sections)
    ? src.sections.map(normalizeBlogSection).filter(Boolean)
    : [];
  return {
    slug: normalizeBlogSlug(src.slug, d.slug),
    title: String(src.title ?? d.title ?? '').trim() || d.title || 'Untitled article',
    excerpt: String(src.excerpt ?? d.excerpt ?? '').trim() || d.excerpt || '',
    date: String(src.date ?? d.date ?? '').trim() || d.date || '',
    cover_image: String(src.cover_image ?? d.cover_image ?? '').trim(),
    sections: sections.length ? sections : Array.isArray(d.sections) ? d.sections : [],
  };
}

export function mergeBlogContent(fromApi) {
  const src = fromApi && typeof fromApi === 'object' ? fromApi : {};
  const section_title =
    String(src.section_title || '').trim() || BLOG_CONTENT_DEFAULTS.section_title;
  const section_subtitle =
    String(src.section_subtitle || '').trim() || BLOG_CONTENT_DEFAULTS.section_subtitle;

  let posts;
  if (Array.isArray(src.posts) && src.posts.length > 0) {
    posts = src.posts
      .slice(0, MAX_BLOG_POSTS)
      .map((p, i) => normalizeBlogPost(p, BLOG_POSTS[i] || BLOG_POSTS[BLOG_POSTS.length - 1]));
  } else {
    posts = BLOG_POSTS.map((p) => normalizeBlogPost(p, p));
  }

  return { section_title, section_subtitle, posts };
}

export function getBlogPostBySlug(slug, blogContent) {
  const merged = mergeBlogContent(blogContent);
  const key = String(slug || '').trim();
  return merged.posts.find((p) => p.slug === key) || null;
}
